import React, { useState } from 'react';
import { Upload, FileUp, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdminBulkUpload = ({ token, onSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const rows = text.split('\n').filter(row => row.trim());
        
        if (rows.length < 2) {
          toast.error('CSV file is empty or invalid');
          return;
        }

        const headers = rows[0].split(',').map(h => h.trim());
        const requiredHeaders = ['name', 'brand', 'description', 'price', 'stock_quantity', 'category_id'];
        
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        if (missingHeaders.length > 0) {
          toast.error(`Missing required columns: ${missingHeaders.join(', ')}`);
          return;
        }

        const products = [];
        for (let i = 1; i < rows.length; i++) {
          const values = rows[i].split(',').map(v => v.trim());
          if (values.length !== headers.length) continue;

          const product = {};
          headers.forEach((header, index) => {
            product[header] = values[index];
          });

          // Convert numeric fields
          product.price = parseFloat(product.price) || 0;
          product.discount = parseFloat(product.discount) || 0;
          product.stock_quantity = parseInt(product.stock_quantity) || 0;
          product.images = product.images ? product.images.split(';').map(url => url.trim()) : [];
          product.status = product.status || 'active';


          // Normalize optional fields for backend validation
          if (!product.subcategory_id) delete product.subcategory_id;
          products.push(product);
        }

        setPreview({ total: products.length, products: products.slice(0, 5) });
        
      } catch (error) {
        console.error('CSV parsing error:', error);
        toast.error('Failed to parse CSV file');
      }
    };

    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    if (!preview || preview.products.length === 0) {
      toast.error('No products to import');
      return;
    }

    setUploading(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      for (const product of preview.products) {
        try {
          await axios.post(`${API_URL}/products`, product, {
            headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
          });
          successCount++;
        } catch (error) {
          console.error('Failed to import product:', product.name, error);
          errorCount++;
        }
      }

      toast.success(`Imported ${successCount} products successfully`);
      if (errorCount > 0) {
        toast.error(`Failed to import ${errorCount} products`);
      }
      
      setPreview(null);
      if (onSuccess) onSuccess();
      
    } catch (error) {
      toast.error('Bulk import failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const template = `name,brand,description,price,discount,stock_quantity,category_id,subcategory_id,images,ingredients,usage_instructions,warnings,status
Sample Product,Sample Brand,Product description here,29.99,10,100,your-category-id,your-subcategory-id,https://example.com/image.jpg,Natural ingredients,Apply daily,For external use only,active`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'product_import_template.csv';
    link.click();
    toast.success('Template downloaded');
  };

  return (
    <div className="space-y-6">
      <h2 className="font-serif text-3xl font-bold">Bulk Product Import</h2>

      <Card>
        <CardHeader>
          <CardTitle>Upload CSV File</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
            <FileUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <Label htmlFor="csv-upload" className="cursor-pointer">
              <div className="mb-4">
                <p className="font-semibold mb-2">Click to upload CSV file</p>
                <p className="text-sm text-muted-foreground">or drag and drop</p>
              </div>
              <input
                id="csv-upload"
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button type="button">
                <Upload className="w-4 h-4 mr-2" />
                Choose File
              </Button>
            </Label>
          </div>

          <div className="flex items-start gap-2 p-4 bg-muted/30 rounded-lg">
            <AlertCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-semibold mb-1">CSV Format Requirements:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Required columns: name, brand, description, price, stock_quantity, category_id</li>
                <li>Optional columns: discount, subcategory_id, images (semicolon-separated URLs)</li>
                <li>First row must be headers</li>
              </ul>
            </div>
          </div>

          <Button variant="outline" onClick={downloadTemplate} className="w-full">
            <FileUp className="w-4 h-4 mr-2" />
            Download CSV Template
          </Button>

          {/* Preview */}
          {preview && (
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-3">Preview ({preview.total} products)</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {preview.products.map((product, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-muted/50 rounded">
                    <div>
                      <p className="font-medium text-sm">{product.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {product.brand} - ${product.price}
                      </p>
                    </div>
                    <Badge variant="success">Ready</Badge>
                  </div>
                ))}
                {preview.total > 5 && (
                  <p className="text-sm text-muted-foreground text-center pt-2">
                    ... and {preview.total - 5} more products
                  </p>
                )}
              </div>
              <Button 
                onClick={handleBulkImport} 
                disabled={uploading}
                className="w-full mt-4"
              >
                {uploading ? 'Importing...' : `Import ${preview.total} Products`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};